import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAssignmentsByUser,
  fetchAssignmentById,
  fetchOrderById,
  updateAssignment,
  addAssignmentRemark,
  appendOrderStatus,
} from "../services/assignment.service";
import { ORDER_STATUS_REMARK_MAP } from "../constants/assignment.constants";

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const loadAssignments = createAsyncThunk(
  "assignments/loadAll",
  async (assignedTo, { rejectWithValue }) => {
    try {
      return await fetchAssignmentsByUser(assignedTo);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const loadAssignmentById = createAsyncThunk(
  "assignments/loadOne",
  async ({ assignmentId, currentUser }, { rejectWithValue }) => {
    try {
      const assignment = await fetchAssignmentById(assignmentId);
      if (!assignment) return rejectWithValue("NOT_FOUND");
      if (assignment.assignedTo !== currentUser)
        return rejectWithValue("UNAUTHORIZED");
      const order = await fetchOrderById(assignment.orderId);
      return { assignment, order };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const submitAssignmentUpdate = createAsyncThunk(
  "assignments/submitUpdate",
  async (
    { assignmentId, orderId, updatedItems, newStatus, currentUser, assignedTo },
    { rejectWithValue }
  ) => {
    try {
      await updateAssignment(assignmentId, updatedItems, newStatus);

      if (newStatus) {
        const remarkFn = ORDER_STATUS_REMARK_MAP[newStatus];
        const remarks = remarkFn ? remarkFn(assignedTo) : newStatus;
        await appendOrderStatus(orderId, {
          updatedBy: currentUser,
          assignmentId,
          remarks,
        });
      }

      return { assignmentId, updatedItems, newStatus };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const submitRemark = createAsyncThunk(
  "assignments/addRemark",
  async ({ assignmentId, comment, currentUser }, { rejectWithValue }) => {
    try {
      const remark = {
        comment,
        updatedBy: currentUser,
        updatedAt: {
          type: "firestore/timestamp/1.0",
          seconds: Math.floor(Date.now() / 1000),
          nanoseconds: 0,
        },
      };
      await addAssignmentRemark(assignmentId, remark);
      return { assignmentId, remark };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const assignmentsSlice = createSlice({
  name: "assignments",
  initialState: {
    list: [],
    listLoading: false,
    listError: null,

    selected: null,       // { assignment, order }
    detailLoading: false,
    detailError: null,    // "NOT_FOUND" | "UNAUTHORIZED" | message

    updateLoading: false,
    updateError: null,

    remarkLoading: false,
    remarkError: null,

    statusFilter: "All",
  },
  reducers: {
    setStatusFilter(state, action) {
      state.statusFilter = action.payload;
    },
    clearSelected(state) {
      state.selected = null;
      state.detailError = null;
    },
    clearUpdateState(state) {
      state.updateLoading = false;
      state.updateError = null;
    },
  },
  extraReducers: (builder) => {
    // ── loadAssignments ──
    builder
      .addCase(loadAssignments.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
      })
      .addCase(loadAssignments.fulfilled, (state, { payload }) => {
        state.listLoading = false;
        state.list = payload;
      })
      .addCase(loadAssignments.rejected, (state, { payload }) => {
        state.listLoading = false;
        state.listError = payload;
      });

    // ── loadAssignmentById ──
    builder
      .addCase(loadAssignmentById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
        state.selected = null;
      })
      .addCase(loadAssignmentById.fulfilled, (state, { payload }) => {
        state.detailLoading = false;
        state.selected = payload;
      })
      .addCase(loadAssignmentById.rejected, (state, { payload }) => {
        state.detailLoading = false;
        state.detailError = payload;
      });

    // ── submitAssignmentUpdate ──
    builder
      .addCase(submitAssignmentUpdate.pending, (state) => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(submitAssignmentUpdate.fulfilled, (state, { payload }) => {
        state.updateLoading = false;
        const { assignmentId, updatedItems, newStatus } = payload;

        // Update in the list
        const idx = state.list.findIndex((a) => a.id === assignmentId);
        if (idx !== -1) {
          state.list[idx].items = updatedItems;
          if (newStatus) state.list[idx].currentStatus = newStatus;
        }

        // Update in selected
        if (state.selected?.assignment?.id === assignmentId) {
          state.selected.assignment.items = updatedItems;
          if (newStatus) state.selected.assignment.currentStatus = newStatus;
        }
      })
      .addCase(submitAssignmentUpdate.rejected, (state, { payload }) => {
        state.updateLoading = false;
        state.updateError = payload;
      });

    // ── submitRemark ──
    builder
      .addCase(submitRemark.pending, (state) => {
        state.remarkLoading = true;
        state.remarkError = null;
      })
      .addCase(submitRemark.fulfilled, (state, { payload }) => {
        state.remarkLoading = false;
        const { assignmentId, remark } = payload;
        if (state.selected?.assignment?.id === assignmentId) {
          state.selected.assignment.remarks = [
            ...(state.selected.assignment.remarks || []),
            remark,
          ];
        }
      })
      .addCase(submitRemark.rejected, (state, { payload }) => {
        state.remarkLoading = false;
        state.remarkError = payload;
      });
  },
});

export const { setStatusFilter, clearSelected, clearUpdateState } =
  assignmentsSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectAllAssignments = (state) => state.assignments.list;
export const selectStatusFilter = (state) => state.assignments.statusFilter;

export const selectFilteredAssignments = (state) => {
  const { list, statusFilter } = state.assignments;
  if (statusFilter === "All") return list;
  return list.filter((a) => a.currentStatus === statusFilter);
};

export const selectStatusCounts = (state) => {
  const counts = {};
  state.assignments.list.forEach(({ currentStatus }) => {
    counts[currentStatus] = (counts[currentStatus] || 0) + 1;
  });
  return counts;
};

export const selectSelected = (state) => state.assignments.selected;
export const selectDetailLoading = (state) => state.assignments.detailLoading;
export const selectDetailError = (state) => state.assignments.detailError;
export const selectUpdateLoading = (state) => state.assignments.updateLoading;
export const selectUpdateError = (state) => state.assignments.updateError;
export const selectListLoading = (state) => state.assignments.listLoading;
export const selectRemarkLoading = (state) => state.assignments.remarkLoading;

export default assignmentsSlice.reducer;
